// src/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";

const AuthCtx = createContext();
const API_URL = process.env.REACT_APP_API_URL || "";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("pvara_user");
      const token = localStorage.getItem("token");
      if (stored && token) return JSON.parse(stored);
      return null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem("pvara_user", JSON.stringify(user));
  }, [user]);

  async function login({ username, password }) {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // Required so the backend's Set-Cookie (HttpOnly screening_jwt)
        // is honoured by the browser. Without this, target=_blank
        // navigations to /api/cv/{token} have no session and 401.
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      // OTP-enabled accounts: password ok, but a one-time code is required next.
      if (data.requires2FA) {
        return { ok: false, requires2FA: true, userId: data.userId };
      }

      if (data.success) {
        applyLoginPayload(data);
        return { ok: true };
      }

      return { ok: false, message: data.message || "Invalid credentials" };
    } catch (error) {
      console.error('Login failed:', error);
      return { ok: false, message: "Unable to connect to server" };
    }
  }

  // Persist token + user from a successful login / 2FA response.
  function applyLoginPayload(data) {
    const { token, user: userData } = data;
    localStorage.setItem('token', token);
    const userPayload = {
      username: userData.username,
      role: userData.role,
      name: userData.fullName || userData.username,
      email: userData.email,
    };
    setUser(userPayload);
    return userPayload;
  }

  // Step 2 of 2FA: exchange the emailed code for a session.
  async function verify2fa({ userId, otpCode }) {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, otpCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        applyLoginPayload(data);
        return { ok: true };
      }
      const msg = (data && data.detail && data.detail.message) || data.message || "Invalid verification code";
      return { ok: false, message: msg };
    } catch (error) {
      console.error('2FA verify failed:', error);
      return { ok: false, message: "Unable to connect to server" };
    }
  }

  async function resendOtp(userId) {
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok && data.success,
               message: data.message || (data.detail && data.detail.message) || "" };
    } catch {
      return { ok: false, message: "Unable to connect to server" };
    }
  }

  function logout(reason) {
    if (reason) {
      // Persists across the reload so the LoginGate can render an explanation.
      try { sessionStorage.setItem("auth_logout_reason", reason); } catch {}
    }
    // Best-effort cookie clear on the server (so leaked CV links stop working
    // on this machine immediately). Fire-and-forget — we reload regardless.
    try {
      fetch(`${API_URL}/api/auth/logout`, { method: "POST", credentials: "include" })
        .catch(() => {});
    } catch {}
    setUser(null);
    localStorage.removeItem("pvara_user");
    localStorage.removeItem("token");
    // Refresh the page to fully reset app state and hide authenticated content
    window.location.reload();
  }

  /**
   * Centralised fetch wrapper:
   *   - injects Bearer token from localStorage
   *   - on 401 from any API call, clears the session and reloads to login,
   *     surfacing "Session expired — please sign in again" on the login screen.
   *
   * Use this in place of raw fetch() for any authenticated API call.
   * Returns the original Response so callers can still inspect status/json.
   */
  async function apiFetch(url, init = {}) {
    const token = localStorage.getItem("token") || "";
    const headers = {
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    let res;
    try {
      // credentials:'include' so the screening_jwt cookie travels on every
      // call (same-origin deploy doesn't require this, but it future-proofs
      // a split-host setup and keeps the cookie path consistent).
      res = await fetch(url, { ...init, headers, credentials: "include" });
    } catch (e) {
      // Network errors bubble up unchanged (caller handles).
      throw e;
    }
    if (res.status === 401) {
      // Detected an expired/invalid token. Kick to login with a one-shot reason.
      logout("Session expired — please sign in again.");
      // Throw so the caller's `await res.json()` doesn't run on a torn-down state.
      throw new Error("session-expired");
    }
    return res;
  }

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === "string") roles = [roles];
    return roles.includes(user.role);
  };

  return (
    <AuthCtx.Provider value={{ user, login, verify2fa, resendOtp, logout, hasRole, apiFetch }}>
      {children}
    </AuthCtx.Provider>
  );
}

/**
 * Pop the one-shot "why was I logged out" message (e.g. for the LoginGate to
 * show under its title). Returns null when there's nothing to show.
 */
export function consumeLogoutReason() {
  try {
    const r = sessionStorage.getItem("auth_logout_reason");
    if (r) sessionStorage.removeItem("auth_logout_reason");
    return r;
  } catch {
    return null;
  }
}

export function useAuth() {
  return useContext(AuthCtx);
}
